import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DEFAULT_BATCH_REPEATS, ROUTE_GROUP, Schedule, ScheduleInput, TargetOption, TestsApi } from '../../services/testsApi';
import { ModelChecklist } from './common';
import { TargetPicker, errorText } from './dialogs';
import { REPEAT_OPTIONS } from './RunPanel';

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const scheduleText = (s: Pick<Schedule, 'cadence' | 'day_of_week' | 'hour_utc' | 'enabled'>) =>
  !s.enabled ? 'Paused' : `${s.cadence === 'daily' ? 'Daily' : `${DAYS[s.day_of_week]}s`} at ${String(s.hour_utc).padStart(2, '0')}:00 UTC`;

/** Create or edit a schedule. `scope="platform"` covers every platform route with cases and can email a digest. */
export const ScheduleDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  scope: 'target' | 'platform';
  schedule?: Schedule | null;
  target?: TargetOption | null;
  onClose: () => void;
  onSaved: (s: Schedule) => void;
}> = ({ open, api, scope, schedule, target: fixedTarget, onClose, onSaved }) => {
  const [form, setForm] = useState<ScheduleInput | null>(null);
  const [target, setTarget] = useState<TargetOption | null>(null);
  const [cap, setCap] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setTarget(fixedTarget || null);
    setCap(schedule?.credit_cap ? String(schedule.credit_cap) : '');
    setForm(
      schedule
        ? {
            name: schedule.name, scope: schedule.scope, target_type: schedule.target_type, target_id: schedule.target_id,
            cadence: schedule.cadence, day_of_week: schedule.day_of_week, hour_utc: schedule.hour_utc, models: schedule.models,
            auto_new_models: schedule.auto_new_models, repeats: schedule.repeats, enabled: schedule.enabled,
            email_digest: schedule.email_digest, credit_cap: schedule.credit_cap,
          }
        : {
            name: scope === 'platform' ? 'Weekly platform routes' : fixedTarget ? `Weekly: ${fixedTarget.name}` : 'Weekly tests',
            scope, target_type: fixedTarget?.target_type || null, target_id: fixedTarget?.target_id || null,
            cadence: 'weekly', day_of_week: 0, hour_utc: 6, models: scope === 'platform' ? ['default', ROUTE_GROUP] : ['default'],
            auto_new_models: scope === 'platform',
            repeats: scope === 'platform' ? DEFAULT_BATCH_REPEATS : 1, enabled: true, email_digest: scope === 'platform', credit_cap: null,
          },
    );
  }, [open, schedule, scope, fixedTarget]);

  if (!form) return null;
  const set = (patch: Partial<ScheduleInput>) => setForm(f => (f ? { ...f, ...patch } : f));
  const needsTarget = scope === 'target' && !schedule && !fixedTarget;

  const save = async () => {
    const capValue = Number(cap);
    const body: ScheduleInput = {
      ...form,
      target_type: scope === 'target' ? target?.target_type || form.target_type : null,
      target_id: scope === 'target' ? target?.target_id || form.target_id : null,
      credit_cap: cap && capValue > 0 ? capValue : null,
    };
    setSaving(true);
    setError(null);
    try {
      onSaved(schedule ? await api.updateSchedule(schedule.id, body) : await api.createSchedule(body));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{schedule ? 'Edit schedule' : scope === 'platform' ? 'Schedule platform route tests' : 'Schedule tests'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {scope === 'platform' && (
            <Typography variant="body2" color="text.secondary">
              Tests every platform route that has enabled test cases, on the models below.
            </Typography>
          )}
          {needsTarget && <TargetPicker api={api} value={target} onChange={setTarget} />}
          <TextField label="Name" size="small" value={form.name} onChange={e => set({ name: e.target.value })} fullWidth />
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>How often</InputLabel>
              <Select label="How often" value={form.cadence} onChange={e => set({ cadence: e.target.value as 'weekly' | 'daily' })}>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
              </Select>
            </FormControl>
            {form.cadence === 'weekly' && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Day</InputLabel>
                <Select label="Day" value={form.day_of_week} onChange={e => set({ day_of_week: Number(e.target.value) })}>
                  {DAYS.map((d, i) => (
                    <MenuItem key={d} value={i}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Hour (UTC)</InputLabel>
              <Select label="Hour (UTC)" value={form.hour_utc} onChange={e => set({ hour_utc: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Models
            </Typography>
            <ModelChecklist
              value={form.models}
              onChange={models => set({ models })}
              context={{ defaultModel: target?.default_model }}
              api={api}
              allowRouteGroup={scope === 'platform'}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox size="small" checked={form.auto_new_models} onChange={e => set({ auto_new_models: e.target.checked })} />}
              label={<Typography variant="body2">Also try models added to EasyAI since the last run</Typography>}
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Runs per case
              </Typography>
              <Select size="small" value={form.repeats} onChange={e => set({ repeats: Number(e.target.value) })} sx={{ minWidth: 100 }}>
                {REPEAT_OPTIONS.map(n => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Credit cap per run
              </Typography>
              <TextField size="small" type="number" value={cap} onChange={e => setCap(e.target.value)} placeholder="Default" sx={{ width: 140 }} />
            </Box>
          </Stack>
          {scope === 'platform' && (
            <FormControlLabel
              control={<Checkbox size="small" checked={form.email_digest} onChange={e => set({ email_digest: e.target.checked })} />}
              label={<Typography variant="body2">Email the results to the admin alert recipients</Typography>}
            />
          )}
          <FormControlLabel control={<Switch checked={form.enabled} onChange={e => set({ enabled: e.target.checked })} />} label="On" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !form.name.trim() || !form.models.length || (needsTarget && !target)}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
